import {
  SYSBaseElement,
  TemplateResult,
  html,
} from "../../../../_ovlclient/src"
import { isMobile, logVersion } from "../../../../_ovlclient/src/global/globals"
import { T } from "../../../../_ovlclient/src/textapi"
import { SYSConfig } from "../../../src"
import itflogo from "../../../icons/shellbarlogoitf.png"
import {
  Logout,
  LanguageSet,
  PortalTestingModeSet,
  PermissionHas,
  FeatureInfo,
  PopupClose,
  PopupShow,
  PopupCurrent,
} from "../../../../_ovlclient/src/global/globalapi"
import { ScreenNavigateTo } from "../../../../_ovlclient/src/library/screenapi"
import {
  HookSYSShellbarTitle,
  HookSYSShellbarLanguages,
  HookSYSShellbarLogo,
  HookSYSShellbarMenu,
  HookSYSShellbarCustomBackButton,
} from "../hooks"
import type {
  HookSYSShellbarLanguages_Type,
  HookSYSShellbarMenu_Type,
  SYSShellbarLanguagesGetType,
  SYSShellbarMenuGetType,
} from "../hooks"
import { AppShellGet } from "../../sysdefaulthtmlsnippets01"
import { Data } from "../../../src/dataapi"
import { TableGet } from "../../../src/library/table/tableapi"
import { DialogOkCancel } from "../../../src/library/dialog/dialogapi"
import { SnackAdd } from "../../../src/library/snackapi"
import { unsafeHTML } from "lit-html/directives/unsafe-html.js"

export type SYSShellbarMenuIds = "Menu1" | "Menu2" | "User"

export class CompSYSShellbar extends SYSBaseElement {
  async connectedCallback() {
    await super.connectedCallback()
    const handleTopLevelClick = (e: Event) => {
      PopupClose()
      e.stopPropagation()
    }
    window.addEventListener("click", handleTopLevelClick)
  }

  handleMenuClick = (e: Event, menuId: SYSShellbarMenuIds) => {
    PopupShow(menuId)
    e.stopPropagation()
  }

  handleLanguage = async (e: Event, lang) => {
    await LanguageSet(lang)
  }

  handlePortalTestingMode = async (e: Event) => {
    PortalTestingModeSet(!this.state.ovl.app.isPortalTestingMode)
  }

  handleEPT = async (e: Event) => {
    await ScreenNavigateTo("SYSEPT")
  }

  handleLogout = (e: Event) => {
    Logout()
  }

  getMenuPartHtml = (m: SYSShellbarMenuGetType): TemplateResult => {
    let res
    if (m.type === "separator") {
      res = html`<span class="fd-menu__separator"> </span>
        <span class="fd-menu__title fd-has-type-2"
          ><i>${unsafeHTML(m.description)}</i></span
        >`
    } else {
      res = html` <li
        class="fd-menu__item"
        role="presentation"
        tabindex="0"
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === "Enter") {
            m.handler(e)
            PopupClose()
          }
        }}
      >
        <a
          @click=${m.handler}
          role="menuitem"
          class="fd-menu__link fd-has-type-2"
        >
          <span class="fd-menu__addon-before ${m.icon}"></span>
          <span class="fd-menu__title fd-has-type-1"
            >${unsafeHTML(m.description)}</span
          >
        </a>
      </li>`
    }
    return res
  }

  getMenuContainerHtml = (
    menuId: SYSShellbarMenuIds,
    menus,
    icon: string,
    iconText: string
  ): TemplateResult => {
    let iconOrText
    if (icon) {
      iconOrText = html`<span class="${icon}  fd-has-color-action-2"></span>`
    } else {
      iconOrText = iconText
    }
    let about
    if (menuId === "User") {
      about = html` <div class="ovl-versioninfo" @click=${(e) => logVersion()}>
          <div>
            Frontend: ${SYSConfig._system.versionInfo.frontend.app.version}
            ${isMobile() ? "M" : ""}
          </div>
          <div>
            Backend: ${SYSConfig._system.versionInfo.backend.app.version}
          </div>
        </div>
        <div class="ovl-shellbarabout">
          <a
            href="https://itflies.ch"
            class="itf"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src=${itflogo} alt="" class="icon" />
            powered by itflies
          </a>
        </div>`
    }
    return html` <div class="fd-shellbar__action">
      <div class="fd-popover fd-popover--right">
        <div class="fd-popover__control">
          <button
            class="fd-button fd-shellbar__button fd-has-type-2 fd-has-color-action-2"
            aria-label=""
            @click=${(e) => this.handleMenuClick(e, menuId)}
            aria-controls="DD35G276${menuId}"
            aria-expanded="${PopupCurrent() === menuId}"
            aria-haspopup="true"
          >
            ${iconOrText}
          </button>
        </div>
        <div
          class="fd-popover__body fd-popover__body--right"
          aria-hidden="${PopupCurrent() !== menuId}"
          id="DD35G276${menuId}"
        >
          <nav class="fd-menu">
            <ul class="fd-menu__list" role="menu">
              ${menus}
            </ul>
          </nav>
          ${about}
        </div>
      </div>
    </div>`
  }

  async getUI() {
    let fn = this.actions.app.features?.SYSShellbar?.hooks
    let devHint
    let modeHint
    if (
      this.state.ovl.app.runMode &&
      this.state.ovl.app.runMode !== "Regular"
    ) {
      modeHint = html`<span class="ovl-runmodehint"
        >${this.state.ovl.app.runMode}</span
      >`
    }
    if (SYSConfig._system.isDev) {
      devHint = html`<span
        class="sap-icon--project-definition-triangle-2"
        title="Dev-Mode ON"
      ></span>`
    }
    let demoHint
    if (this.state.ovl.uiState.isDemo) {
      demoHint = html`<span
        class="sap-icon--circle-task-2"
        title="Testenvironment ON"
      ></span>`
    }
    let portalTestingHint
    if (this.state.ovl.app.isPortalTestingMode) {
      portalTestingHint = html`<span
        class="sap-icon--sound-off"
        title=${`Testing-Mode ON ->  If not otherwise indicated Emails will go to: ${this.state.ovl.app.testingEmail}`}
      ></span>`
    } else {
      portalTestingHint = html``
    }

    let hints = html`<div class="ovl-shellbarhints">
      ${devHint}${demoHint}${portalTestingHint}${modeHint}
    </div>`
    let logo

    if (fn?.[HookSYSShellbarLogo]) {
      logo = fn[HookSYSShellbarLogo]()
    }

    let appTitle
    let menu1
    let menu2
    let userMenu
    let sysRefreshButton
    if (this.state.ovl.app.ready) {
      sysRefreshButton = html`
        <comp-sysrefreshbutton> </comp-sysrefreshbutton>
      `

      if (fn?.[HookSYSShellbarTitle]) {
        appTitle = fn?.[HookSYSShellbarTitle]()
      } else {
        appTitle = T("SYSAppTitle")
      }
      if (fn?.[HookSYSShellbarMenu]) {
        //######## User Menu ###########
        let items: SYSShellbarMenuGetType[] = []
        let result = { items, resultIcon: "" }
        fn[HookSYSShellbarMenu]({
          menuId: "User",
          result,
        } as HookSYSShellbarMenu_Type)

        let languageSelector
        // if there is more than 1 available languages display language selector
        let availableLanguages = this.state.ovl.language.availableTranslations
        if (availableLanguages.length > 1) {
          if (fn?.[HookSYSShellbarLanguages]) {
            let langresults: SYSShellbarLanguagesGetType[] = []
            fn[HookSYSShellbarLanguages]({
              availableLanguages,
              result: { items: langresults },
            } as HookSYSShellbarLanguages_Type)
            let languageItems = langresults.map((m) => {
              let part
              if (m.pic) {
                part = html`<img
                  @click=${(e) => this.handleLanguage(e, m.lang)}
                  class="ovl-language-icon"
                  src=${m.pic}
                  width="20"
                  height="16"
                />`
              } else {
                part = html`<span
                  @click=${(e) => this.handleLanguage(e, m.lang)}
                  >${m.lang}</span
                >`
              }
              return part
            })
            languageSelector = html`
              <li class="fd-menu__item">
                <a role="menuitem" class="fd-menu__link fd-has-type-2">
                  <span class="fd-menu__addon-before sap-icon--globe "></span>
                  <span class="fd-menu__title fd-has-type-2">
                    ${languageItems}
                  </span>
                </a>
              </li>
            `
          }
        }

        result.items.push({
          description: T("SYSLoginLogout"),
          icon: "sap-icon--visits",
          handler: this.handleLogout,
        })

        if (this.state.ovl.features.SYSShellbar.config.showAdminMenuPart) {
          // push admin menus to userMenu end
          let adminResults: SYSShellbarMenuGetType[] = []
          if (PermissionHas("SYSTR")) {
            adminResults.push({
              icon: "sap-icon--table-view",
              description: T("SYSTranslationTitle"),
              handler: (e) => {
                ScreenNavigateTo("SYSTranslation")
              },
            })
          }
          if (PermissionHas("SYSAU")) {
            adminResults.push({
              icon: "sap-icon--history",
              description: T("SYSAuditTitle"),
              handler: (e) => {
                ScreenNavigateTo("SYSAudit")
              },
            })
          }

          if (
            this.state.ovl.user.role === "Admin" ||
            FeatureInfo("SYSPortalTesting")
          ) {
            let portalTestingModeMenuText = this.state.ovl.app
              .isPortalTestingMode
              ? "Disable Testingmode"
              : "Enable Testingmode"
            adminResults.push({
              description: portalTestingModeMenuText,
              icon: `sap-icon--${
                this.state.ovl.app.isPortalTestingMode
                  ? "sound-off"
                  : "sound-loud"
              }`,
              handler: this.handlePortalTestingMode,
            })
          }
          if (adminResults.length > 0) {
            adminResults.unshift({ description: "Admin", type: "separator" })
            result.items = result.items.concat(adminResults)
          }
        }
        if (this.state.ovl.app.runMode === "EPT") {
          result.items.push({
            description: "EPT",
            icon: "sap-icon--wrench",
            handler: this.handleEPT,
          })

          result.items.push({
            description: "EPTFileDelete",
            icon: "sap-icon--delete",
            handler: async (e) => {
              if ((await DialogOkCancel("Delete requests.json")) === 2) {
                return
              }
              let tb = TableGet("SYSEPT")
              let endpoint = tb.def.server.endpoint
              await Data(`${endpoint}/delete`)
              SnackAdd("resuests.json deleted")
            },
          })
        }
        result.items.push({
          description: T("SYSVersion"),
          type: "separator",
        })

        if (result.items.length > 0) {
          let icon
          let userInitials
          let userState = this.state.ovl.user
          userInitials =
            (userState?.firstName
              ? userState.firstName.substring(0, 1).toUpperCase()
              : "") +
            (userState?.lastName
              ? userState.lastName.substring(0, 1).toUpperCase()
              : "")
          if (!userInitials) {
            userInitials = userState.email
            if (userInitials) {
              userInitials = userInitials.substring(0, 2).toUpperCase()
            }
          }
          if (!userInitials) {
            icon = "sap-icon--role"
          } else {
            if (this.state.ovl.user.isActor) {
              userInitials = `(${userInitials})`
            }
          }

          userMenu = this.getMenuContainerHtml(
            "User",
            [languageSelector].concat(
              result.items.map((k) => this.getMenuPartHtml(k))
            ),
            icon,
            userInitials
          )
        }

        // ############ Menu1
        items = []
        result = { items, resultIcon: "" }
        fn[HookSYSShellbarMenu]({
          menuId: "Menu1",
          result,
        } as HookSYSShellbarMenu_Type)

        if (result.items.length > 0) {
          menu1 = this.getMenuContainerHtml(
            "Menu1",

            result.items.map((k) => this.getMenuPartHtml(k)),
            result.resultIcon,
            ""
          )
        }

        // ############ Menu2
        items = []
        result = { items, resultIcon: "" }
        fn[HookSYSShellbarMenu]({
          menuId: "Menu2",
          result,
        } as HookSYSShellbarMenu_Type)

        if (result.items.length > 0) {
          menu2 = this.getMenuContainerHtml(
            "Menu2",

            result.items.map((k) => this.getMenuPartHtml(k)),
            result.resultIcon,
            ""
          )
        }
      }
    }
    let backButton

    backButton = html`
      <comp-sysbackbutton
        class="fd-shellbar__group fd-shellbar__group--copilot ovl-margin-corr1"
      >
      </comp-sysbackbutton>
    `
    if (fn?.[HookSYSShellbarCustomBackButton]) {
      backButton = html`<div
        class="fd-shellbar__group fd-shellbar__group--copilot ovl-margin-corr1"
      >
        ${fn[HookSYSShellbarCustomBackButton]().res}
      </div>`
    }
    let shell = html`
      ${backButton}
      <div
        class="fd-shellbar__group fd-shellbar__group--actions ovl-margin-corr1"
      >
        <div class="fd-shellbar__action">
          <comp-sysshellbarhint .mode=${"right"}></comp-sysshellbarhint>
        </div>
        <div class="fd-shellbar__action">${sysRefreshButton}</div>
        ${userMenu} ${menu2} ${menu1}
      </div>
    `

    let resHtml = html`
        <div class="fd-shellbar" tabindex="-1">
              <div class="fd-shellbar__group fd-shellbar__group--product" @click=${(
                e
              ) => {
                e.stopPropagation()
                e.preventDefault()
                if (SYSConfig.init.initalScreen) {
                  ScreenNavigateTo(SYSConfig.init.initalScreen)
                }
              }}>
                <a href="#"  class="fd-shellbar__logo"
                  ><img src="${logo}" width="48" height="24" 
                /></a>
                <span class="fd-shellbar__title ovl-shellbar-title"
                >${appTitle}</span
              >
              &nbsp;&nbsp;
              <span class="fd-shellbar__title"
                ><comp-sysshellbarhint .mode=${"left"}></comp-sysshellbarhint></span>
              &nbsp;&nbsp;
                <span class="fd-shellbar__title"
                  >${hints}</span
                >

              </div>
                ${shell}
              </div>
          </div>
          ${AppShellGet()}
        </div>
      `
    return resHtml
  }
}
